import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';

import { Router } from '@angular/router';
import { ArkUnitOfWork } from 'app/core/core.module';
import { Involvement, InvolvementSkill, Person } from 'app/core/model/entity-model';
import { SharedModule } from 'app/shared/shared.module';
import { DevExtremeModule, DxDataGridComponent } from 'devextreme-angular';
import _ from 'lodash';
import { confirm, alert } from 'devextreme/ui/dialog';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Predicate } from 'breeze-client';
import moment from 'moment';

@Component({
    selector: 'app-record',
    template: `
        <div class="page-content page-fill">
          <div class="row flex-fill">
            <div class="col-4 flex-fill border-end">
              <div class="grid-fill">
                <dx-data-grid
                  #peopleGrid
                  [dataSource]="people"
                  keyExpr="id"
                  [showBorders]="true"
                  (onFocusedRowChanged)="getInvolvements()"
                  >
                  <dxi-column dataField="firstName" [sortIndex]="2" [sortOrder]="'asc'"></dxi-column>
                  <dxi-column dataField="lastName" [sortIndex]="1" [sortOrder]="'asc'"></dxi-column>
        
                  <dxo-filter-row [visible]="true"></dxo-filter-row>
                  <dxo-selection mode="single"></dxo-selection>
                  <dxo-state-storing
                    [enabled]="true"
                    type="sessionStorage"
                    storageKey="involvementRecordGrid"
                  ></dxo-state-storing>
                  <dxo-search-panel [visible]="true"></dxo-search-panel>
                </dx-data-grid>
              </div>
            </div>
            <div class="col-8 flex-fill">
              @if (involvements) {
                <div class="mx-3 grid-fill">
                  <dx-data-grid
                    #grid
                    [dataSource]="involvements"
                    keyExpr="id"
                    [showBorders]="true"
                    (onToolbarPreparing)="onToolbarPreparing($event)"
                    >
                    <dxi-column dataField="whenLocal" dataType="datetime"></dxi-column>
                    <dxi-column dataField="skill.name"></dxi-column>
                    <div *dxTemplate="let data of 'toolbarButtons'">
                      <dx-button text="Add" type="default" (onClick)="edit(involvementModal)"></dx-button>
                      <dx-button
                        text="Edit"
                        type="default"
                        (onClick)="edit(involvementModal, grid)"
                        [disabled]="!grid.focusedRowKey"
                      ></dx-button>
                      <dx-button
                        text="Delete"
                        type="danger"
                        (onClick)="delete(grid)"
                        [disabled]="!grid.focusedRowKey"
                      ></dx-button>
                    </div>
                    <dxo-selection mode="single"></dxo-selection>
                    <dxo-search-panel [visible]="true"></dxo-search-panel>
                  </dx-data-grid>
                </div>
              }
            </div>
          </div>
        </div>
        
        <ng-template #involvementModal>
          <div class="modal-content">
            <div class="modal-header">
              <h4 class="modal-title pull-left">Involvement</h4>
            </div>
            <div class="modal-body">
              <dx-form [formData]="selectedInvolvement">
                <dxi-item dataField="whenLocal" [label]="{ text: 'When' }" [editorOptions]="{ type: 'datetime' }"></dxi-item>
                <dxi-item
                  dataField="skillId"
                  [label]="{ text: 'Skill' }"
                  editorType="dxSelectBox"
                  [editorOptions]="{ items: skills, valueExpr: 'id', displayExpr: 'name' }"
                ></dxi-item>
              </dx-form>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-success" (click)="saveDialog()">Save</button>
              <button type="button" class="btn btn-default" (click)="closeDialog()">Cancel</button>
            </div>
          </div>
        </ng-template>
        `,
    styles: [],
    imports: [DevExtremeModule]
})
export class RecordComponent implements OnInit {
    people: any[];
    selectedPerson: Person;
    skills: InvolvementSkill[];
    involvements: Involvement[];
    defaultWhen: Date;
    onToolbarPreparing = SharedModule.onToolbarPreparing;

    @ViewChild('peopleGrid') peopleGrid: DxDataGridComponent;
    selectedInvolvement: any;

    constructor(private uow: ArkUnitOfWork, private modalService: BsModalService) {}

    async ngOnInit() {
        this.defaultWhen = moment().startOf('day').add(6, 'hour').toDate();
        this.skills = await this.uow.involvementSkills.all('name');
        const query = this.uow.people.baseQuery().where('includeInDirectory', '==', true).select(['firstName', 'lastName', 'id']);
        this.people = (await this.uow.executeQuery(query)) as any[];
    }

    async getInvolvements() {
        this.involvements = await this.uow.involvements.where(new Predicate('personId', '==', this.peopleGrid.focusedRowKey), 'when desc');
    }

    async edit(template: TemplateRef<any>, grid?: DxDataGridComponent) {
        this.selectedInvolvement = grid
            ? await grid.instance.byKey(grid.focusedRowKey)
            : (this.uow.manager.createEntity('Involvement', {
                  when: this.defaultWhen,
                  personId: this.peopleGrid.focusedRowKey,
              }) as Involvement);
        this.editDialog(template);
    }

    async delete(grid: DxDataGridComponent) {
        this.selectedInvolvement = await grid.instance.byKey(grid.focusedRowKey);
        if (
            await confirm(`Are you sure you want to delete the "${this.selectedInvolvement.skill.name}" involvement?`, 'Delete Involvement')
        ) {
            this.selectedInvolvement.entityAspect.setDeleted();
            await this.uow.commit();
            this.selectedInvolvement = null;
            await this.getInvolvements();
        }
    }

    async editDialog(template: TemplateRef<any>) {
        const modalRef = this.modalService.show(template, { backdrop: 'static', class: 'modal-sm', focus: true });
        this.modalService.onHidden.subscribe((reason) => {
            this.closeDialog();
        });
    }

    async saveDialog() {
        await this.uow.commit();
        if (this.selectedInvolvement.when) this.defaultWhen =  this.selectedInvolvement.when;

        await this.getInvolvements();
        this.closeDialog();
    }

    closeDialog() {
        this.uow.rollback();
        this.modalService.hide(1);
    }
}
